// 外注発注リソースの宣言。既存の subcontract.service をそのまま呼ぶ（ブラウザ側＝本人の権限で実行）。
//
// 【業務フロー】外注発注は外注先（取引先マスタの subcontractor 区分）を指定して作成する。
// 案件（orderCode）は任意。金額は明細から計算し、集計同期（order.subcontractTotal）は本体サービスの責務。
import { z } from 'zod';
import { Timestamp } from 'firebase/firestore';
import {
  fetchSubcontractOrders,
  createSubcontractOrder,
  updateSubcontractOrder,
  type SubcontractOrderCreateInput,
  type SubcontractOrderUpdateInput,
} from '@modules/subcontract/services/subcontract.service';
import type { SubcontractOrder } from '@modules/subcontract/types';
import { findOrderByCode } from './order.resource';
import type { ID } from '@shared/types';
import type { ListFilters, ResourceDescriptor, WriteResult } from './types';
import {
  MAX_ROWS,
  aiDocItemSchema,
  calcDocTotals,
  dateString,
  normalizeCode,
  resolveCustomer,
  resolveEmployee,
  toDateString,
  toDocLineItems,
  toTimestamp,
} from './shared';

/** 外注発注1件を、LLM が読む最小限の形へ射影する。 */
function toCompactSubcontractOrder(s: SubcontractOrder) {
  return {
    code: s.code,
    title: s.title,
    total: s.total, // 税込合計
    orderDate: toDateString(s.orderDate),
  };
}

async function list(filters: ListFilters): Promise<unknown> {
  const orders = await fetchSubcontractOrders();

  const keyword = filters.keyword;
  const filtered = keyword
    ? orders.filter((s) => s.title.includes(keyword) || s.code.includes(keyword))
    : orders;

  return {
    count: filtered.length,
    truncated: filtered.length > MAX_ROWS,
    subcontract_orders: filtered.slice(0, MAX_ROWS).map(toCompactSubcontractOrder),
  };
}

async function get(rawCode: string): Promise<unknown> {
  const code = normalizeCode(rawCode);
  const orders = await fetchSubcontractOrders();
  const found = orders.find((s) => normalizeCode(s.code) === code);
  if (!found) {
    return { found: false, note: `外注発注番号「${rawCode}」は見つかりませんでした` };
  }
  return { found: true, subcontract_order: toCompactSubcontractOrder(found) };
}

// ── 書込（C-2）─────────────────────────────────────────────────

const writeCreateSchema = z.object({
  // 【原則】Firestore の ID を LLM に組ませない。名前か顧客コードを受け、ツール側で解決する
  subcontractor: z.string().min(1, 'subcontractor（外注先の名前または顧客コード）は必須'),
  title: z.string().min(1, 'title（件名）は必須'),
  orderCode: z.string().optional(),
  items: z.array(aiDocItemSchema).min(1).max(30),
  notes: z.string().optional(),
  description: z.string().optional(),
  assignee: z.string().min(1).optional(),
  orderDate: dateString.optional(),
  deliveryDate: dateString.optional(),
});

const writeUpdateSchema = z
  .object({
    subcontractor: z.string().min(1).optional(),
    title: z.string().min(1).optional(),
    notes: z.string().optional(),
    description: z.string().optional(),
    assignee: z.string().min(1).optional(),
    orderDate: dateString.optional(),
    deliveryDate: dateString.optional(),
    /** 渡すと明細を「全て置き換える」（追記ではない）。金額は自動再計算される。 */
    items: z.array(aiDocItemSchema).min(1).max(30).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'changes に変更する項目を1つ以上入れる' });

/** code から更新対象の外注発注を引き当てる（get と同じ正規化・完全一致のみ）。 */
async function findByCode(rawCode: string): Promise<SubcontractOrder | null> {
  const code = normalizeCode(rawCode);
  const orders = await fetchSubcontractOrders();
  return orders.find((s) => normalizeCode(s.code) === code) ?? null;
}

export const subcontractOrderResource: ResourceDescriptor = {
  name: 'subcontract_order',
  label: '外注発注',
  list,
  get,
  write: {
    fields: [
      { name: 'subcontractor', label: '外注先', type: 'string', required: true, note: '外注先の名前または顧客コードをそのまま渡す。IDは不要' },
      { name: 'title', label: '件名', type: 'string', required: true },
      { name: 'orderCode', label: '案件番号', type: 'ref', resource: 'order', note: '作成時のみ・案件番号（任意）' },
      { name: 'items', label: '明細', type: 'items', required: true, note: '配列 [{name,quantity,unit?,unitPrice}]。更新では全置き換え・金額は自動計算' },
      { name: 'notes', label: '備考', type: 'string' },
      { name: 'description', label: '摘要', type: 'string' },
      { name: 'assignee', label: '担当者', type: 'string', note: '担当者名で指定（IDは不要）' },
      { name: 'orderDate', label: '発注日', type: 'date', note: 'YYYY-MM-DD' },
      { name: 'deliveryDate', label: '納品予定日', type: 'date', note: 'YYYY-MM-DD' },
    ],
    createSchema: writeCreateSchema,
    // 支払（paidAt/paymentDate/billId）・status は画面の責務。AIには開放しない
    updateSchema: writeUpdateSchema,
    async create(data, userId: ID): Promise<WriteResult> {
      const input = data as z.infer<typeof writeCreateSchema>;

      // ① 外注先を名前/コードから解決（存在しないIDでの作成を許さない）
      const subcontractor = await resolveCustomer(input.subcontractor, 'subcontractor');

      // ② 案件は任意。指定されたら解決する（存在しない番号での作成は許さない）
      const order = input.orderCode ? await findOrderByCode(input.orderCode) : null;
      if (input.orderCode && !order) {
        throw new Error(
          `案件「${input.orderCode}」が見つかりません。先に create_record(order) で受注を作成し、その code を orderCode に指定してください`
        );
      }

      // ③ 金額は明細から計算（税計算モードは案件から継承。案件なしは floor）
      const taxCalcMode = order?.taxCalcMode ?? 'floor';
      const totals = calcDocTotals(input.items, taxCalcMode);

      // ④ 作成（採番・監査ログ・受注集計の同期は本体サービスの責務）
      const created = await createSubcontractOrder(
        {
          subcontractorId: subcontractor.id,
          title: input.title,
          orderId: order?.id ?? null,
          // 担当者は名前→ID解決。指定が無ければ実行者を自動設定する（人がレビュー時に変更できる）
          assignedUserId: input.assignee !== undefined ? (await resolveEmployee(input.assignee)).id : userId,
          ...(input.description !== undefined ? { description: input.description } : {}),
          ...(input.deliveryDate !== undefined ? { deliveryDate: toTimestamp(input.deliveryDate) } : {}),
          amount: totals.subtotal,
          taxAmount: totals.taxAmount,
          total: totals.total,
          // 帳票表示・受注集計（grandtotal 基準）にも同じ金額を渡す
          subtotal: totals.subtotal,
          totalgst: totals.taxAmount,
          grandtotal: totals.total,
          orderDate: input.orderDate !== undefined ? toTimestamp(input.orderDate) : Timestamp.now(),
          lineItems: toDocLineItems(input.items),
          taxCalcMode,
          ...(input.notes !== undefined ? { notes: input.notes } : {}),
          aiPendingReview: true,
        } as SubcontractOrderCreateInput,
        userId
      );
      return { id: created.id, code: created.code };
    },
    async update(code, changes, userId: ID): Promise<WriteResult | null> {
      const target = await findByCode(code);
      if (!target) return null;
      const input = changes as z.infer<typeof writeUpdateSchema>;

      const patch: SubcontractOrderUpdateInput = { aiPendingReview: true };
      // 外注先・担当者は名前→ID解決（IDをLLMに組ませない）
      if (input.subcontractor !== undefined) {
        patch.subcontractorId = (await resolveCustomer(input.subcontractor, 'subcontractor')).id;
      }
      if (input.assignee !== undefined) patch.assignedUserId = (await resolveEmployee(input.assignee)).id;
      if (input.title !== undefined) patch.title = input.title;
      if (input.notes !== undefined) patch.notes = input.notes;
      if (input.description !== undefined) patch.description = input.description;
      if (input.orderDate !== undefined) patch.orderDate = toTimestamp(input.orderDate);
      if (input.deliveryDate !== undefined) patch.deliveryDate = toTimestamp(input.deliveryDate);

      // 明細は「全置き換え」。金額は明細から再計算する（create と同じ対応で
      // amount/taxAmount/total と subtotal/totalgst/grandtotal の両系列へ渡す）
      if (input.items && input.items.length > 0) {
        const taxCalcMode = target.taxCalcMode ?? 'floor';
        const totals = calcDocTotals(input.items, taxCalcMode);
        patch.lineItems = toDocLineItems(input.items);
        patch.amount = totals.subtotal;
        patch.taxAmount = totals.taxAmount;
        patch.total = totals.total;
        patch.subtotal = totals.subtotal;
        patch.totalgst = totals.taxAmount;
        patch.grandtotal = totals.total;
      }

      // 受注集計（order.subcontractTotal）の同期は本体サービスの責務
      await updateSubcontractOrder(target.id, patch, userId);
      return { id: target.id, code: target.code };
    },
    link: (id) => `/subcontracts/${id}/edit`,
  },
};
